
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { adminApi } from "@/services/ApiService";
import * as ST from "@/components/controls/s-table/exports";
import { BindingMode } from "@/components/controls/s-table/exports";
import STable from "@/components/controls/s-table/sTable.vue";
import TextEdit from "@/components/controls/text-edit.vue";
import CheckEdit from "@/components/controls/check-edit.vue";
import NameEdit from "@/components/admin/standardProducts/name-edit.vue";
import ImageEdit from "@/components/admin/standardProducts/image-edit.vue";
import {
  StandardProductRecord,
  createRecord,
  hasChanges,
  toSaveRow,
} from "@/components/admin/standardProducts/StandardProductRecord";

@Component({ components: { STable } })
export default class StandardProducts extends Vue {
  dataSource: StandardProductRecord[] = [];
  saving: boolean = false;

  tableSettings: ST.Settings<StandardProductRecord> = {
    dataSource: this.dataSource,
    pagination: { pageSize: 50 },
    showNewRow: false,
    loading: true,
    filter: {
      filterString: "",
      filterProperties: ["plu", "name"],
    },
    addRowClasses: (record) => {
      if (hasChanges(record)) return ["standard-edited-row"];
      if (record.saved) return ["standard-saved-row"];
      return [];
    },
    columns: [
      {
        title: "Spara",
        edit: CheckEdit,
        width: 70,
        editData: (record) => ({
          editable: true,
          immediate: true,
          updateEmitter: "valueChanged",
          dataBinding: {
            propertyName: "selected",
          },
          props: {
            defaultValue: record.selected,
          },
        }),
      },
      {
        title: "Bild",
        edit: ImageEdit,
        width: 90,
        editData: (record) => ({
          editable: true,
          immediate: true,
          updateEmitter: "valueChanged",
          dataBinding: {
            mode: BindingMode.Unbound,
            propertyName: "pickedImageId",
            customUnboundDataSet: (value: number | null) => {
              record.pickedImageId = value;
              record.selected = true;
            },
          },
          props: {
            record: record,
          },
        }),
      },
      {
        title: "Namn",
        edit: NameEdit,
        editData: (record) => ({
          editable: true,
          immediate: true,
          updateEmitter: "valueChanged",
          dataBinding: {
            mode: BindingMode.Unbound,
            propertyName: "name",
            customUnboundDataSet: (value: string) => {
              record.name = value;
              record.selected = true;
            },
          },
          props: {
            record: record,
          },
        }),
      },
      {
        title: "PLU",
        edit: TextEdit,
        width: 120,
        editData: (record) => ({
          editable: false,
          dataBinding: {
            propertyName: "plu",
          },
        }),
      },
      {
        title: "Butiker",
        edit: TextEdit,
        width: 120,
        editData: (record) => ({
          editable: false,
          info:
            record.stores === 0
              ? "Ingen aktiv butik använder längre denna PLU"
              : undefined,
          dataBinding: {
            propertyName: "stores",
          },
        }),
      },
    ],
  };

  get changeCount() {
    return this.dataSource.filter(hasChanges).length;
  }

  public async mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    await this.loadData();
  }

  public beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  public beforeRouteLeave(to: any, from: any, next: any) {
    if (this.changeCount === 0) return next();
    this.$confirm({
      title: "Osparade ändringar",
      content: "Du har ändringar som inte är sparade. Vill du lämna sidan ändå?",
      okText: "Lämna sidan",
      cancelText: "Stanna kvar",
      onOk: () => next(),
      onCancel: () => next(false),
    });
  }

  onBeforeUnload(e: BeforeUnloadEvent) {
    if (this.changeCount === 0) return;
    e.preventDefault();
    e.returnValue = "";
  }

  onSearch(e: any) {
    if (!this.tableSettings.filter) return;
    this.tableSettings.filter.filterString = e.srcElement.value ?? "";
  }

  async loadData() {
    this.tableSettings.loading = true;
    try {
      let result = await adminApi.getStandardProducts();
      let records = (result.data.products ?? []).map(createRecord);
      records.sort(
        (a, b) =>
          (b.stores ?? 0) - (a.stores ?? 0) || a.name.localeCompare(b.name, "sv")
      );
      this.dataSource.splice(0);
      this.dataSource.push(...records);
    } finally {
      this.tableSettings.loading = false;
    }
  }

  async save() {
    let save = this.dataSource.filter((c) => c.selected).map(toSaveRow);
    let remove = this.dataSource
      .filter((c) => c.saved && !c.selected)
      .map((c) => c.plu ?? "");
    this.saving = true;
    try {
      let result = await adminApi.saveValidatedProducts({ save, remove });
      this.notify(
        `${result.data.saved ?? 0} sparade, ${result.data.removed ?? 0} borttagna`,
        "Sparat"
      );
    } catch (error) {
      this.$notification.error({
        message: "Kunde inte spara",
        description:
          error?.response?.status === 400 && error.response.data
            ? error.response.data
            : "Ingenting har sparats. Försök igen.",
        placement: "bottomRight",
        duration: 8,
      });
      return;
    } finally {
      this.saving = false;
    }
    await this.loadData();
  }

  public notify(description: string, message: string) {
    this.$notification.open({
      message: message,
      description: description,
      placement: "bottomRight",
      duration: 4,
    });
  }
}
